<script>
  import { onMount } from 'svelte';
  import UrlInput from './UrlInput.svelte';
  import RecentBinders from './RecentBinders.svelte';
  import { authState } from '../../lib/stores/auth.svelte.js';
  import {
    getUnifiedBinderList,
    getCachedBinderList,
    cacheBinderList,
    getLocalBinders,
    removeRecentBinder,
    pinBinder,
    unpinBinder,
  } from '../../lib/services/recent-binders.js';
  import { syncRecentBinders, syncMyBinders } from '../../lib/services/user-data.js';
  import { DISCORD_BOT_CLIENT_ID } from '../../lib/logic/config.js';
  import { cancelTrade } from '../../lib/services/trade-service.js';

  let recentBinders = $state([]);
  let hasLoadedCloud = false;

  // Listen for external binder list changes (e.g., trade cancelled from sidebar)
  onMount(() => {
    const handler = () => refreshBinders();
    window.addEventListener('recentBindersUpdated', handler);
    return () => window.removeEventListener('recentBindersUpdated', handler);
  });

  /** Anonymous: the local list. Signed in: the cloud list, cached for next visit's first paint. */
  async function loadBinders() {
    const user = authState.user;
    if (!user || user.isAnonymous) return getLocalBinders();
    const binders = await getUnifiedBinderList();
    cacheBinderList(user.uid, binders);
    return binders;
  }

  async function refreshBinders() {
    recentBinders = await loadBinders();
  }

  $effect(() => {
    if (!authState.ready) return;
    const user = authState.user;

    if (!user || user.isAnonymous) {
      recentBinders = getLocalBinders();
    } else if (!hasLoadedCloud) {
      hasLoadedCloud = true;
      // Paint last visit's list now; the load below replaces it. (Write-only:
      // reading recentBinders here would make this effect depend on it.)
      const cached = getCachedBinderList(user.uid);
      if (cached) recentBinders = cached;
      (async () => {
        try {
          // Merge any local-only data into the cloud before reading it back
          await syncRecentBinders();
          await syncMyBinders();
          await refreshBinders();
        } catch (e) {
          console.warn('[HomePage] Failed to load cloud binders:', e);
        }
      })();
    }
  });

  async function handleStar(binder) {
    const url = binder.binderUrl || binder.url;
    if (binder.isStarred) {
      await unpinBinder(url);
    } else {
      await pinBinder(url);
    }
    await refreshBinders();
  }

  async function handleRemove(binder) {
    const url = binder.binderUrl || binder.url;
    if (binder.tradeId) {
      await cancelTrade(binder.tradeId);
    }
    await removeRecentBinder(url);
    // Remove from local list immediately for instant feedback
    recentBinders = recentBinders.filter((b) => b.url !== binder.url);
  }

  async function handleCancelTrade(binder) {
    if (!binder.tradeId) return;
    try {
      await cancelTrade(binder.tradeId);
      await refreshBinders();
    } catch (e) {
      console.error('[HomePage] Failed to cancel trade:', e);
    }
  }
</script>

<main class="home-page">
  <UrlInput />

  <a
    href="https://discord.com/oauth2/authorize?client_id={DISCORD_BOT_CLIENT_ID}&permissions=84992&scope=bot"
    target="_blank"
    rel="noopener noreferrer"
    class="discord-banner"
  >
    <svg
      width="20"
      height="16"
      viewBox="0 0 71 55"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"
      />
    </svg>
    <span>Add Binder Browser to your Discord server</span>
    <svg
      class="arrow"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  </a>
  <RecentBinders
    binders={recentBinders}
    onStar={handleStar}
    onRemove={handleRemove}
    onCancelTrade={handleCancelTrade}
  />

  <div class="discord-footer">
    <a
      href="https://discord.gg/PnmrCrbgfC"
      target="_blank"
      rel="noopener noreferrer"
      title="Join our Discord community"
      class="discord-icon"
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 71 55"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"
        />
      </svg>
    </a>
  </div>
</main>

<style>
  .home-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-height: 100vh;
    padding-top: 20vh;
    position: relative;
    z-index: 1;
    box-sizing: border-box;
  }

  @media (min-width: 768px) {
    .home-page {
      padding-top: 35vh;
    }
  }

  .discord-footer {
    margin-top: auto;
    padding: 2rem 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    animation: fadeInUp 0.8s ease-out 0.3s forwards;
    opacity: 0;
  }

  .discord-footer .discord-icon {
    display: block;
    width: 48px;
    color: #9ca3af;
    transition:
      color var(--dur-base) var(--ease-standard),
      transform var(--dur-base) var(--ease-standard);
  }

  .discord-footer .discord-icon:hover {
    color: #6366f1;
    transform: scale(1.1);
  }

  .discord-banner {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 1.2rem;
    margin-top: 1rem;
    background: linear-gradient(135deg, #5865f2 0%, #4752c4 100%);
    color: #fff;
    border-radius: 999px;
    text-decoration: none;
    font-size: 0.78rem;
    font-weight: 500;
    transition:
      transform var(--dur-base) var(--ease-standard),
      box-shadow var(--dur-base) var(--ease-standard),
      background var(--dur-base) var(--ease-standard);
    animation: fadeInUp 0.6s ease-out 0.2s forwards;
    opacity: 0;
    box-shadow: 0 1px 4px rgba(88, 101, 242, 0.2);
  }

  .discord-banner:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(88, 101, 242, 0.4);
    background: linear-gradient(135deg, #6570f7 0%, #5261d4 100%);
  }

  .discord-banner .arrow {
    opacity: 0.7;
    transition: transform 0.2s ease;
  }

  .discord-banner:hover .arrow {
    transform: translateX(3px);
    opacity: 1;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
